// add the beginning of your app entry
import 'vite/modulepreload-polyfill';

import config from '@/services/config.service';
import { setContextProvider } from '@/services/logger.service';

import Vue from 'vue';
import 'vue-class-component/hooks'; // import hooks type to enable auto-complete
// ref: https://class-component.vuejs.org/guide/additional-hooks.html

import './styles/global.scss';

import App from '@/App.vue';
import router from '@/router';
import store, { loadPersistentState } from '@/store';
import {env} from '@/services/env.service';
import {switchTheme} from '@/services/theming.service';

// @WS_UIkit
import { get } from 'lodash-es';
import { isBrowser } from '@loopia-group/utils';
import { deviceInfo, setupGlobalMixins } from '@loopia-group/vue2-utils';
import { setConfigValues, Bundler, setupGoogleAPI } from '@loopia-group/services';
import {Screen} from 'quasar';

config.bundler = Bundler.rollup;

setConfigValues(config);

loadPersistentState();

import { i18nService } from '@WS_UIkit';


import '@/utilities/filters';
import {cartApiServiceSetup, fetchCart} from '@/services/cart/cart-api.service';
import { changeAxiosConfig } from '@/services/network.service';

import { updateStateFromBackend } from '@/services/order.service';

import PortalVue from 'portal-vue';


import ResizeSensor from '@/vendor/resize-sensor/ResizeSensor.js';
import { hostingTutorialSetProducts } from '@/services/hosting-tutorial.service';
import {setupIwp} from '@/modules/iwp';
import {registerCartItemSettingsInDrawer, setupCheckout} from '@/modules/checkout';

import {StoreActions} from '@/store/const.enum';

import {setupAnalytics} from '@/services/analytics.service';
import {setupMessage} from '@/services/message.service';
import {setupLoader} from '@loopia-group/assets';
import {setupDeveloper} from '@/modules/developer';
import {setupI18n} from '@/services/i18n';
import {setupCrowdin} from '@/services/crowdin';
import {setupHotjar} from '@/services/hotjar';
import {setupPinia} from '@/services/pinia';
import {setupQuasar} from '@/services/quasar';
import {setupPortalOutlet, setupComponents} from '@loopia-group/components-vue2';
import '@loopia-group/components-vue2/dist/style.css';
import {registerProfilesSettingsInDrawer, setupProfiles} from '@/modules/profiles';
import { setupRecapitulationCartItems } from '@/modules/checkout';
import {setupPaymentMethod} from '@/modules/payment-method';
import {setupServiceSettings} from '@/modules/service-settings';
import {isInIframe, notifyParentAboutClicks} from '@/utilities/iframeCommunication.ts';


(window as any).ResizeSensor = ResizeSensor;

if (config.env === 'staging') {
  // must be before store loading to have store features in devtools work correctly
  Vue.config.devtools = true;
}
setupLoader(() => 'rollup', () => config.theme);


// Sentry
setContextProvider(
  () => ({
    cart: store.state.cart,
    ui: store.state.ui,
    bpsLength: get(store, 'state.billingProfiles.length', 0),
    dpsLength: get(store, 'state.domainProfiles.length', 0),
    login: get(store, 'state.cart.user.login', '_unknown_user_'),
  }),
  true
);



Vue.use(PortalVue);
Vue.config.productionTip = false;



deviceInfo.init();

Vue.mixin(setupGlobalMixins(Screen, get));


// ===========>Services
changeAxiosConfig({ baseURL: config.apiUrl });
const pinia = setupPinia();
setupQuasar();
const i18nPromise = setupI18n();
setupPortalOutlet(Vue);

setupAnalytics();
setupMessage(i18nService.i18n);
setupCrowdin();
setupHotjar();

// @ts-ignore
if(window?.google) {
  // @ts-ignore
  setupGoogleAPI(window.google);
}



//===========>MODULES
/// "I want a website" flow module
setupIwp(router);
setupCheckout(router);
setupProfiles(store);
setupPaymentMethod(store);
setupServiceSettings(store);
setupRecapitulationCartItems();
registerCartItemSettingsInDrawer();
registerProfilesSettingsInDrawer();
setupDeveloper(router);
cartApiServiceSetup();


const mountTarget = (config.appRootElm && document.querySelector(config.appRootElm)) || '#main-content';
const vueConfig = {
  router,
  store,
  // TODO: Fix TS error caused by Pinia: TS2769
  // @ts-ignore
  pinia,
  i18n: i18nService.i18n,
  methods: {
    switchTheme
  },

  beforeCreate() {
    // aligning breakpoints with SCSS ones
    // https://quasar.dev/options/screen-plugin#configuration
    this.$q.screen.setSizes({ md: 768, lg: 1260 });


    // initially load cart
    fetchCart();
    // Fetch and load domain checker categories
    this.$store.dispatch(StoreActions.FETCH_DOMAIN_CATEGORIES);
    // Load hosting tutorial products into store
    hostingTutorialSetProducts();

    // automatically fetch cart changes on tab getting focus by user
    // solves scenario when user handles order in multiple tabs or devices
    if (isBrowser && !env.VITE_DISABLE_AUTO_UPDATE_ON_REFOCUS) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState) {
          updateStateFromBackend();
        }
      });
      // also immediate
      updateStateFromBackend(true);
    }
  },
  render: h => h(App),
  provide: {
    wsDev: Vue.prototype.$wsDev,
    messageService: Vue.prototype.$messageService,
  },
};

i18nPromise.then((i18next) => {
  setupComponents({i18next});

  new Vue(vueConfig).$mount(mountTarget);

  if (isInIframe()) {
    notifyParentAboutClicks();
  }
});
